// Settling a purchase, apart from the screen that announces it.
//
// Stripe answers the browser before the webhook has finished creating the
// order, so what was bought is not owned yet at the moment the buyer is
// thanked. The wait used to live inside the thank-you screen: closing it
// stopped the polling, and the product behind stayed locked until the page
// was reloaded.
//
// It lives here instead. It is started once per payment, it keeps going
// whether or not anything is watching, and it says so when it is done —
// `PURCHASE_COMPLETE_TOPIC` carries the pids that changed hands, so the
// product page, the player and the library can catch up on their own.
import PubSub from "pubsub-js"
import { PaymentStatus, getAuth, setAuth, refreshStoreAccount } from "../storeApi"

export const PURCHASE_COMPLETE_TOPIC = "sf-purchase-complete"
export const PURCHASE_STATE_TOPIC = "sf-purchase-state"

const POLL_INTERVAL = 1000
const MAX_POLLS = 40

// one run per payment intent, and its last known state for whoever asks late
const runs = new Map()

/** `{ state: "validating" | "done" | "failed", pids }` for a payment. */
export const purchaseState = (paymentIntentId) => runs.get(paymentIntentId)?.state || null

/**
 * Follow a payment until the order exists. Called again for the same payment,
 * it joins the run already under way rather than starting a second one.
 * @returns {Promise<{state: string, pids: string[]}>}
 */
export function settlePurchase(paymentIntentId, { pids = [] } = {}) {
  if (!paymentIntentId) return Promise.resolve({ state: "failed", pids })
  const existing = runs.get(paymentIntentId)
  if (existing) return existing.promise

  const publish = (state) => {
    const run = runs.get(paymentIntentId)
    if (run) run.state = state
    PubSub.publish(PURCHASE_STATE_TOPIC, { paymentIntentId, state, pids })
    if (state === "done") PubSub.publish(PURCHASE_COMPLETE_TOPIC, { paymentIntentId, pids })
  }

  const promise = (async () => {
    for (let poll = 0; poll <= MAX_POLLS; poll++) {
      try {
        const status = await PaymentStatus(paymentIntentId)
        if (status?.processed) {
          // the order may belong to an account created during checkout: the
          // answer carries its credentials
          const current = getAuth()
          if (status.auth?.auth_token && (!current?.auth_token || String(current._id) !== String(status.auth._id))) setAuth(status.auth)
          await refreshStoreAccount()
          publish("done")
          return { state: "done", pids }
        }
      } catch {
        // the webhook may still be working; ask again
      }
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL))
    }
    // the order will still arrive — it is the waiting that gave up
    publish("failed")
    return { state: "failed", pids }
  })()

  runs.set(paymentIntentId, { promise, state: "validating" })
  PubSub.publish(PURCHASE_STATE_TOPIC, { paymentIntentId, state: "validating", pids })
  return promise
}

/**
 * Run `handler` when a purchase settles. Returns the unsubscribe function, so
 * it fits straight into an effect.
 */
export function onPurchaseComplete(handler) {
  const token = PubSub.subscribe(PURCHASE_COMPLETE_TOPIC, (_, data) => handler(data))
  return () => PubSub.unsubscribe(token)
}
